import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AnnouncementMutationPayload,
  AnnouncementQueryParams,
  announcementsService,
} from '@/services/announcementsService'
import { AnnouncementModel } from '@/models/announcement.model'
import { PaginatedResponse } from '@/models/templates/pagination.model'

export const announcementKeys = {
  all: ['announcements'] as const,
  lists: () => [...announcementKeys.all, 'list'] as const,
  list: (filters: AnnouncementQueryParams) => [...announcementKeys.lists(), filters] as const,
  details: () => [...announcementKeys.all, 'detail'] as const,
  detail: (id: string) => [...announcementKeys.details(), id] as const,
  learner: () => [...announcementKeys.all, 'learner'] as const,
  learnerList: (filters: AnnouncementQueryParams) => [...announcementKeys.learner(), filters] as const,
}

const useNormalizedFilters = (params: AnnouncementQueryParams = {}) =>
  useMemo(() => {
    const normalized: AnnouncementQueryParams = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      normalized[key as keyof AnnouncementQueryParams] = value as never
    })
    return normalized
  }, [JSON.stringify(params)])

export function useAnnouncements(params: AnnouncementQueryParams = {}) {
  const filters = useNormalizedFilters(params)
  return useQuery<PaginatedResponse<AnnouncementModel>>({
    queryKey: announcementKeys.list(filters),
    queryFn: () => announcementsService.getAnnouncements(filters),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useAnnouncement(announcementId: string | undefined) {
  return useQuery<AnnouncementModel>({
    queryKey: announcementId ? announcementKeys.detail(announcementId) : announcementKeys.details(),
    queryFn: () => {
      if (!announcementId) throw new Error('Announcement ID is required')
      return announcementsService.getAnnouncementById(announcementId)
    },
    enabled: Boolean(announcementId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCourseAnnouncements(
  courseId: string | undefined,
  params: Omit<AnnouncementQueryParams, 'courseId'> = {},
) {
  const filters = useNormalizedFilters(params)
  return useQuery<PaginatedResponse<AnnouncementModel>>({
    queryKey: courseId ? announcementKeys.list({ ...filters, courseId }) : announcementKeys.lists(),
    queryFn: () => {
      if (!courseId) throw new Error('Course ID is required')
      return announcementsService.getAnnouncements({ ...filters, courseId })
    },
    enabled: Boolean(courseId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useLearnerAnnouncements(params: AnnouncementQueryParams = {}) {
  const filters = useNormalizedFilters(params)
  return useQuery<PaginatedResponse<AnnouncementModel>>({
    queryKey: announcementKeys.learnerList(filters),
    queryFn: () => announcementsService.getLearnerAnnouncements(filters),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: AnnouncementMutationPayload) => announcementsService.createAnnouncement(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.learner() })
    },
  })
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ announcementId, payload }: { announcementId: string; payload: AnnouncementMutationPayload }) =>
      announcementsService.updateAnnouncement(announcementId, payload),
    onSuccess: (announcement) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(announcement.id) })
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.learner() })
    },
  })
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (announcementId: string) => announcementsService.deleteAnnouncement(announcementId),
    onSuccess: (_, announcementId) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(announcementId) })
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.learner() })
    },
  })
}

export function useAnnouncementPinState() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ announcementId, action }: { announcementId: string; action: 'pin' | 'unpin' }) =>
      announcementsService.setPinState(announcementId, action),
    onSuccess: (announcement) => {
      queryClient.invalidateQueries({ queryKey: announcementKeys.detail(announcement.id) })
      queryClient.invalidateQueries({ queryKey: announcementKeys.lists() })
      queryClient.invalidateQueries({ queryKey: announcementKeys.learner() })
    },
  })
}
